// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useCallback, useRef, useState } from 'react';

import type { AutodeskElementTagId, AutodeskExternalId } from '@evolvemep/api-ts';
import { Box, Card, DataList, getThemeColor, Loader, Text, useMantineTheme } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import type { RenderedTagsSnapshot } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createRenderedTagsStore';
import {
  formatProperty,
  hasBlankValue,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/ElementTagProperties';
import { TAG_LABEL_WIDTH_PX } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/helpers';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil } from 'helpers/isNotNil';

const TAG_DRAG_THRESHOLD_PX = 4;
/**
 * Stable callbacks passed from `DimensioningExtension` down through the React tree.
 * All functions are created once in `mountReactRoot` and never change reference.
 */
export type TagInteractionCallbacks = {
  /** Drag threshold was crossed — lock viewer navigation, suppress leaders. */
  onDragStart: (elementTagId: AutodeskElementTagId) => void;
  /** Drag released — convert coords to world space, update head, rebuild leaders. */
  onDragCommit: (
    elementTagId: AutodeskElementTagId,
    clientX: number,
    clientY: number,
    grabOffsetX: number,
    grabOffsetY: number,
  ) => void;
  /** Drag cancelled (e.g. `pointercancel`) without committing. */
  onDragCancel: (elementTagId: AutodeskElementTagId) => void;
  /** Label tapped without dragging. */
  onTagClick: (externalId: AutodeskExternalId) => void;
  /**
   * Returns the viewer container's bounding rect.
   * Used to convert client coordinates to container-relative positions during drag.
   */
  getContainerRect: () => DOMRect;
};

type Props = {
  tag: RenderedTagsSnapshot;
  callbacks: TagInteractionCallbacks;
};

/** A single tag label. */
export const TagLabel = ({ tag, callbacks }: Props) => {
  const { isSaving } = useAutodeskExtensionStoreSnapshot('elementTags');

  const [isDragging, setIsDraggingState] = useState(false);

  /**
   * A reference to the tag element, used only for pointer-gesture bookkeeping (grab offset) and the imperative drag
   * fast path below.
   */
  const elRef = useRef<HTMLDivElement>(null);

  // Pointer event state is stored in refs because pointer events trigger changes faster than React's render cycle.
  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);
  const activePointerRef = useRef<number | null>(null);
  const grabOffsetRef = useRef({ x: 0, y: 0 });
  const startClientRef = useRef({ x: 0, y: 0 });

  const setIsDragging = useCallback((dragging: boolean) => {
    isDraggingRef.current = dragging;
    setIsDraggingState(dragging);
  }, []);

  /** Clears the transient drag offset */
  const clearDragOffset = useCallback(() => {
    const el = elRef.current;
    if (el) el.style.translate = '';
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0 || activePointerRef.current !== null || isSaving) return;
      e.preventDefault();
      e.stopPropagation();

      const el = elRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      grabOffsetRef.current = {
        x: e.clientX - (rect.left + rect.width / 2),
        y: e.clientY - (rect.top + rect.height / 2),
      };
      startClientRef.current = { x: e.clientX, y: e.clientY };
      setIsDragging(false);
      didDragRef.current = false;
      activePointerRef.current = e.pointerId;

      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [isSaving, setIsDragging],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      const el = elRef.current;
      if (!el) return;

      const distance = Math.hypot(e.clientX - startClientRef.current.x, e.clientY - startClientRef.current.y);

      if (!isDraggingRef.current) {
        if (distance < TAG_DRAG_THRESHOLD_PX) return;
        setIsDragging(true);
        didDragRef.current = true;
        callbacks.onDragStart(tag.elementTagId);
      }

      e.preventDefault();
      // Directly mutate the DOM — bypass React reconciliation entirely during drag.
      // Written as an offset from the store-driven `left`/`top`, so React stays the sole owner of the committed
      // position and `clearDragOffset` can always undo the gesture.
      const containerRect = callbacks.getContainerRect();
      const offsetX = e.clientX - grabOffsetRef.current.x - containerRect.left - tag.x;
      const offsetY = e.clientY - grabOffsetRef.current.y - containerRect.top - tag.y;
      el.style.translate = `${offsetX}px ${offsetY}px`;
    },
    [callbacks, setIsDragging, tag.elementTagId, tag.x, tag.y],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      activePointerRef.current = null;

      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // Already released.
      }

      if (isDraggingRef.current) {
        setIsDragging(false);
        callbacks.onDragCommit(
          tag.elementTagId,
          e.clientX,
          e.clientY,
          grabOffsetRef.current.x,
          grabOffsetRef.current.y,
        );
        // Cleared after the commit so the store is the last writer: `onDragCommit` publishes the
        // released position, which comes back as new `tag.x`/`tag.y`, and then the override goes
        // away. Both happen in this one discrete-event task, so nothing paints in between.
        clearDragOffset();
      } else if (!didDragRef.current) {
        callbacks.onTagClick(tag.externalId);
      }
    },
    [callbacks, clearDragOffset, setIsDragging, tag.externalId, tag.elementTagId],
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (activePointerRef.current !== e.pointerId) return;
      activePointerRef.current = null;

      if (isDraggingRef.current) {
        setIsDragging(false);
        // Nothing is committed, so snap back to the store-driven position.
        clearDragOffset();
        callbacks.onDragCancel(tag.elementTagId);
      }
    },
    [callbacks, clearDragOffset, setIsDragging, tag.elementTagId],
  );

  /** Safety net for a gesture that ends without `pointerup` or `pointercancel`. */
  const handleLostPointerCapture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      /** If this is a different pointer than the one that started the gesture, ignore it */
      if (activePointerRef.current !== e.pointerId) return;
      activePointerRef.current = null;
      if (!isDraggingRef.current) return;

      setIsDragging(false);
      clearDragOffset();
      callbacks.onDragCancel(tag.elementTagId);
    },
    [callbacks, clearDragOffset, setIsDragging, tag.elementTagId],
  );

  const theme = useMantineTheme();

  return (
    <Card
      ref={elRef}
      pos="absolute"
      bg="gray.2"
      withBorder
      p={0}
      w={`${TAG_LABEL_WIDTH_PX}px`}
      display={tag.visible ? 'flex' : 'none'}
      style={
        tag.visible
          ? {
              top: `${tag.y}px`,
              left: `${tag.x}px`,
              transform: 'translate(-50%, -50%)',
              flexDirection: 'row',
              pointerEvents: 'auto',
              borderColor: getThemeColor(isDragging ? theme.primaryColor : 'gray.4', theme),
              overflow: 'hidden',
            }
          : undefined
      }
    >
      <Box
        p={4}
        onPointerMove={handlePointerMove}
        onPointerDown={handlePointerDown}
        onPointerCancel={handlePointerCancel}
        onPointerUp={handlePointerUp}
        onLostPointerCapture={handleLostPointerCapture}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isSaving ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
          backgroundColor: isDragging ? getThemeColor(theme.primaryColor, theme) : undefined,
          opacity: isSaving ? 0.4 : undefined,
        }}
      >
        <EvolveIcon icon="Draggable" size="sm" color={isDragging ? 'gray.9' : 'gray'} />
      </Box>

      {isNil(tag.propertyValues) ? (
        <Loader type="dots" size="sm" my="xs" mx="auto" />
      ) : (
        <DataList
          orientation="vertical"
          withDivider
          gap={2}
          p="xs"
          pl={0}
          w="100%"
          mah={225}
          style={{ overflowY: 'auto' }}
        >
          {tag.propertyValues.map(([propName, value]) => {
            const formattedValue = formatProperty({ value }, '[blank]');
            return (
              <DataList.Item key={propName}>
                <DataList.ItemLabel fz="xs">{propName}</DataList.ItemLabel>
                <DataList.ItemValue>
                  <Text
                    c={hasBlankValue(value) ? 'yellow.6' : undefined}
                    fw={700}
                    lineClamp={2}
                    title={!hasBlankValue(value) ? formattedValue : undefined}
                  >
                    {formattedValue}
                  </Text>
                </DataList.ItemValue>
              </DataList.Item>
            );
          })}
        </DataList>
      )}
    </Card>
  );
};
